/**
 * Documents Panel
 * Manages NDA, MSA, SOW, and other contract documents for an opportunity.
 * Supports upload, AI term extraction, signing workflow, and versioning.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Upload,
  Trash2,
  Eye,
  Brain,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Plus,
  Loader2,
  ExternalLink,
  Shield,
  FileSignature,
} from 'lucide-react';
import {
  documentsAPI,
  OpportunityDocument,
  DocumentType,
  SignedStatus,
} from '@/services/api/salesPipelineAPI';
import { FileUploadWidget, UploadedFile } from './FileUploadWidget';

interface DocumentsPanelProps {
  opportunityId: string;
}

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  nda: 'NDA',
  msa: 'MSA',
  sow: 'SOW',
  proposal: 'Proposal',
  amendment: 'Amendment',
  addendum: 'Addendum',
  other: 'Other',
};

const SIGNED_STATUS_CONFIG: Record<SignedStatus, { label: string; color: string; icon: React.ReactNode }> = {
  unsigned: { label: 'Unsigned', color: 'bg-gray-100 text-gray-600', icon: <Clock className="w-3 h-3" /> },
  pending_signature: { label: 'Pending Signature', color: 'bg-yellow-100 text-yellow-700', icon: <Clock className="w-3 h-3" /> },
  signed: { label: 'Signed', color: 'bg-green-100 text-green-700', icon: <CheckCircle className="w-3 h-3" /> },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-600', icon: <AlertCircle className="w-3 h-3" /> },
  voided: { label: 'Voided', color: 'bg-red-100 text-red-600', icon: <XCircle className="w-3 h-3" /> },
};

export const DocumentsPanel: React.FC<DocumentsPanelProps> = ({ opportunityId }) => {
  const [documents, setDocuments] = useState<OpportunityDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [extracting, setExtracting] = useState<string | null>(null);
  const [expandedTerms, setExpandedTerms] = useState<string | null>(null);

  // Upload form state
  const [newDoc, setNewDoc] = useState({
    documentType: 'sow' as DocumentType,
    documentName: '',
    description: '',
    extractTerms: true,
  });
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [uploading, setUploading] = useState(false);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const docs = await documentsAPI.listDocuments(opportunityId);
      setDocuments(docs);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleUpload = async () => {
    if (!newDoc.documentName || !uploadedFile) return;
    setUploading(true);
    try {
      // If local file, upload via FormData; if cloud file, pass URL reference
      if (uploadedFile.source === 'local' && uploadedFile.file) {
        const formData = new FormData();
        formData.append('file', uploadedFile.file);
        formData.append('document_type', newDoc.documentType);
        formData.append('document_name', newDoc.documentName);
        if (newDoc.description) formData.append('description', newDoc.description);
        formData.append('extract_terms', String(newDoc.extractTerms));

        await documentsAPI.uploadDocument(opportunityId, {
          documentType: newDoc.documentType,
          documentName: newDoc.documentName,
          description: newDoc.description || undefined,
          fileUrl: '', // Will be set by backend after processing upload
          extractTerms: newDoc.extractTerms,
          file: uploadedFile.file,
        });
      } else {
        // Cloud file - pass the cloud URL
        await documentsAPI.uploadDocument(opportunityId, {
          documentType: newDoc.documentType,
          documentName: newDoc.documentName,
          description: newDoc.description || undefined,
          fileUrl: uploadedFile.cloudUrl || '',
          extractTerms: newDoc.extractTerms,
          cloudSource: uploadedFile.source,
          cloudFileId: uploadedFile.cloudFileId,
        });
      }
      setShowUploadForm(false);
      setNewDoc({ documentType: 'sow', documentName: '', description: '', extractTerms: true });
      setUploadedFile(null);
      await loadDocuments();
    } catch (err) {
      console.error('Failed to upload document:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleExtractTerms = async (docId: string) => {
    setExtracting(docId);
    try {
      await documentsAPI.extractTerms(opportunityId, docId);
      await loadDocuments();
    } catch (err) {
      console.error('Failed to extract terms:', err);
    } finally {
      setExtracting(null);
    }
  };

  const handleUpdateSignedStatus = async (docId: string, status: SignedStatus) => {
    try {
      await documentsAPI.updateDocument(opportunityId, docId, {
        signedStatus: status,
        signedDate: status === 'signed' ? new Date().toISOString() : undefined,
      });
      await loadDocuments();
    } catch (err) {
      console.error('Failed to update document:', err);
    }
  };

  const handleDelete = async (docId: string) => {
    if (!confirm('Delete this document?')) return;
    try {
      await documentsAPI.deleteDocument(opportunityId, docId);
      await loadDocuments();
    } catch (err) {
      console.error('Failed to delete document:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-blue-600 mr-2" />
        <span className="text-sm text-gray-500">Loading documents...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Contract Documents</h3>
        <button
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
        >
          <Plus className="w-4 h-4" />
          Add Document
        </button>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
          {/* File Upload Widget */}
          <FileUploadWidget
            onFileSelected={(file) => {
              setUploadedFile(file);
              // Auto-fill name from filename if empty
              if (!newDoc.documentName) {
                const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
                setNewDoc(prev => ({ ...prev, documentName: nameWithoutExt }));
              }
            }}
            showCloudPickers={true}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
              <select
                value={newDoc.documentType}
                onChange={(e) => setNewDoc({ ...newDoc, documentType: e.target.value as DocumentType })}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                {Object.entries(DOC_TYPE_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
              <input
                type="text"
                value={newDoc.documentName}
                onChange={(e) => setNewDoc({ ...newDoc, documentName: e.target.value })}
                placeholder="Document name"
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Description (optional)</label>
            <input
              type="text"
              value={newDoc.description}
              onChange={(e) => setNewDoc({ ...newDoc, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-md text-sm"
            />
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={newDoc.extractTerms}
                onChange={(e) => setNewDoc({ ...newDoc, extractTerms: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded"
              />
              <Brain className="w-3.5 h-3.5 text-purple-500" />
              AI Extract Key Terms
            </label>
            <div className="flex gap-2">
              <button onClick={() => { setShowUploadForm(false); setUploadedFile(null); }} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button
                onClick={handleUpload}
                disabled={!newDoc.documentName || !uploadedFile || uploading}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
              >
                {uploading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document List */}
      {documents.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No documents yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => {
            const statusConfig = SIGNED_STATUS_CONFIG[doc.signedStatus];
            return (
              <div key={doc.id} className="border rounded-lg p-3 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {doc.documentType === 'nda' && <Shield className="w-5 h-5 text-orange-500" />}
                      {doc.documentType === 'msa' && <FileSignature className="w-5 h-5 text-blue-500" />}
                      {doc.documentType === 'sow' && <FileText className="w-5 h-5 text-green-500" />}
                      {!['nda', 'msa', 'sow'].includes(doc.documentType) && <FileText className="w-5 h-5 text-gray-400" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{doc.documentName}</span>
                        <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600 uppercase">
                          {DOC_TYPE_LABELS[doc.documentType]}
                        </span>
                        <span className={`flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded ${statusConfig.color}`}>
                          {statusConfig.icon}
                          {statusConfig.label}
                        </span>
                      </div>
                      {doc.description && <p className="text-xs text-gray-500 mt-0.5">{doc.description}</p>}
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                        {doc.uploadDate && <span>Uploaded {new Date(doc.uploadDate).toLocaleDateString()}</span>}
                        {doc.effectiveDate && <span>Effective {new Date(doc.effectiveDate).toLocaleDateString()}</span>}
                        {doc.expiryDate && <span>Expires {new Date(doc.expiryDate).toLocaleDateString()}</span>}
                        <span>v{doc.version}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {doc.fileUrl && (
                      <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="p-1 text-gray-400 hover:text-blue-600">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    {doc.signedStatus === 'unsigned' && (
                      <button
                        onClick={() => handleUpdateSignedStatus(doc.id, 'pending_signature')}
                        className="p-1 text-gray-400 hover:text-yellow-600"
                        title="Mark as pending signature"
                      >
                        <FileSignature className="w-4 h-4" />
                      </button>
                    )}
                    {doc.signedStatus === 'pending_signature' && (
                      <button
                        onClick={() => handleUpdateSignedStatus(doc.id, 'signed')}
                        className="p-1 text-gray-400 hover:text-green-600"
                        title="Mark as signed"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    )}
                    {(!doc.aiExtractionStatus || doc.aiExtractionStatus === 'pending' || doc.aiExtractionStatus === 'failed') && (
                      <button
                        onClick={() => handleExtractTerms(doc.id)}
                        disabled={extracting === doc.id}
                        className="p-1 text-gray-400 hover:text-purple-600"
                        title="AI Extract Terms"
                      >
                        {extracting === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
                      </button>
                    )}
                    <button onClick={() => handleDelete(doc.id)} className="p-1 text-gray-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* AI Extraction Status */}
                {doc.aiExtractionStatus === 'completed' && doc.aiExtractedTerms && (
                  <div className="mt-2 pt-2 border-t">
                    <button
                      onClick={() => setExpandedTerms(expandedTerms === doc.id ? null : doc.id)}
                      className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
                    >
                      <Brain className="w-3 h-3" />
                      AI Extracted Terms
                      {doc.aiExtractionConfidence && (
                        <span className="text-gray-400 ml-1">({Math.round(doc.aiExtractionConfidence * 100)}% confidence)</span>
                      )}
                    </button>
                    {expandedTerms === doc.id && (
                      <div className="mt-2 p-2 bg-purple-50 rounded text-xs space-y-1">
                        {Object.entries(doc.aiExtractedTerms).map(([key, value]) => (
                          <div key={key} className="flex gap-2">
                            <span className="font-medium text-purple-700 min-w-[120px]">{key.replace(/_/g, ' ')}:</span>
                            <span className="text-gray-700">
                              {typeof value === 'object' ? JSON.stringify(value, null, 0) : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {doc.aiExtractionStatus === 'processing' && (
                  <div className="mt-2 pt-2 border-t flex items-center gap-1 text-xs text-purple-500">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    AI extraction in progress...
                  </div>
                )}
                {doc.aiExtractionStatus === 'failed' && (
                  <div className="mt-2 pt-2 border-t flex items-center gap-1 text-xs text-red-500">
                    <AlertCircle className="w-3 h-3" />
                    AI extraction failed
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
